/**
 * Phase 105-Agent-C — useAgentLocation hook.
 *
 * Streams the agent's `navigator.geolocation` watch into
 * `POST /api/agent/location` every ~15 seconds. Throttle on the client
 * side mirrors the backend's 10-sec throttle defense-in-depth. Failed
 * POSTs land in the IndexedDB offline queue and are drained as soon as
 * a subsequent POST succeeds.
 *
 * The hook is pause-aware: pass `enabled=false` (e.g. when the agent
 * is offline) and it stops both the geo watch and the upstream POSTs
 * cleanly.
 */
import { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { enqueueLocationPing, drainLocationQueue } from "../utils/offlineLocationQueue";

const PING_INTERVAL_MS = 15_000;

export default function useAgentLocation({ enabled = true } = {}) {
  const [position, setPosition] = useState(null); // {lat, lng, accuracy_m, heading, speed_mps}
  const [error, setError] = useState(null);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [queuedCount, setQueuedCount] = useState(0);
  const watchIdRef = useRef(null);
  const sendTimerRef = useRef(null);
  const latestRef = useRef(null);

  // 1. Start / stop the geolocation watch.
  useEffect(() => {
    if (!enabled) {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return undefined;
    }
    if (!navigator.geolocation) {
      setError("Geolocation unavailable in this browser.");
      return undefined;
    }
    setError(null);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          heading: pos.coords.heading ?? undefined,
          speed_mps: pos.coords.speed ?? undefined,
        };
        latestRef.current = p;
        setPosition(p);
      },
      (err) => setError(err?.message || "Couldn't get location"),
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 12_000 },
    );
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled]);

  // 2. Posting loop.
  useEffect(() => {
    if (!enabled) {
      if (sendTimerRef.current) {
        clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
      return undefined;
    }
    const send = async () => {
      const p = latestRef.current;
      if (!p) return;
      try {
        await api.post("/agent/location", p);
        setLastSentAt(new Date().toISOString());
        // Drain anything queued from a prior offline window. The queue
        // is tiny (cap 10) so this is cheap.
        await drainLocationQueue(async (row) => {
          try { await api.post("/agent/location", row); return true; }
          catch { return false; }
        });
        setQueuedCount(0);
      } catch (e) {
        // 429 is fine — backend throttle is defense-in-depth. Don't queue.
        const status = e?.response?.status;
        if (status === 429) return;
        await enqueueLocationPing(p);
        setQueuedCount((c) => Math.min(10, c + 1));
      }
    };
    // Send once immediately if we already have a position, then interval.
    send();
    sendTimerRef.current = setInterval(send, PING_INTERVAL_MS);
    return () => {
      if (sendTimerRef.current) {
        clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
    };
  }, [enabled]);

  return {
    lat: position?.lat ?? null,
    lng: position?.lng ?? null,
    accuracy_m: position?.accuracy_m ?? null,
    heading: position?.heading ?? null,
    speed_mps: position?.speed_mps ?? null,
    error,
    paused: !enabled,
    lastSentAt,
    queuedCount,
  };
}
